import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import * as carrouselAction from "../../../../redux/actions/carrousel.action";
import { useTranslation } from "react-i18next";
import List from "../../../list/list";
import placeholder from "../../../../assets/images/placeholder.jpg";
import { fetchSASToken } from "../../../../services/SASTokenService";
import Select from "react-select";
import { getTheme } from "../../../../helpers/select.theme";
import {
  formatGroupLabel,
  getOnlyMemberAndGroupOptions,
} from "../../../../helpers/select";
import { customStylesForTable } from "../../../../helpers/select.custom";
import PortalVisibilitySelect from "../../../PortalVisibilitySelect/PortalVisibilitySelect";

const carrouselFields = [
  //list will sort by the first element by default
  "title",
  "active",
  "description",
  "url",
  "createdAt",
];
const CarrouselList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [token, setToken] = useState(null);
  const [memberOptions, setMemberOptions] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");


  const { carrousels, meta, listLoading, error } = useSelector((state) => ({
    carrousels: state.carrousel.carrouselList,
    meta: state.carrousel.carrouselMetadata,
    listLoading: state.carrousel.listLoading,
    error: state.carrousel.error,
  }));

  const initialParams = {
    fieldTofilter: "title",
    filter: "",
    fieldToSort: "order",
    sortDirection: "desc",
    page: 1,
    pageSize: 8,
    memberFilters: { all: true, members: [], groups: [] },
  };

  useEffect(() => {
    carrouselAction.fetchAll(dispatch, initialParams);
    const fetchToken = async () => {
      setToken(await fetchSASToken());
    };
    fetchToken();
  }, [dispatch]);

  useEffect(() => {
    if (carrousels?.data && carrousels?.data?.length > 0) {
      const options = carrousels?.data?.reduce(
        (obj, { id, attributes: { locations, location_groups } }) => {
          obj[id] = getOnlyMemberAndGroupOptions(location_groups, locations);
          return obj;
        },
        {}
      );
      setMemberOptions(options);
    }
  }, [carrousels]);

  const isAll = (members, member_groups) =>
    !members.data.length && !member_groups.data.length;

  const handleVisibilityChange = (carrouselId) => async (selectedOption) => {
    try {
      // Find the carousel being updated
      const carousel = carrousels?.data?.find(c => c.id === carrouselId);
      if (!carousel) {
        setErrorMessage('Carousel not found');
        setTimeout(() => setErrorMessage(''), 5000);
        return;
      }

      const { attributes } = carousel;
      const formData = new FormData();

      // Include all required fields from the existing carousel
      const formBody = {
        title: attributes.title,
        description: attributes.description,
        url: attributes.url || "",
        active: attributes.active,
        portal_visibility: selectedOption.value,
        locations: attributes.locations?.data?.map(loc => loc.id) || [],
        location_groups: attributes.location_groups?.data?.map(group => group.id) || [],
        is_all_locations: !attributes.locations?.data?.length && !attributes.location_groups?.data?.length,
        user_scope: attributes.user_scope || "all_users",
        portal_user_roles: attributes.portal_user_roles?.map(role => role.id) || [],
      };

      formData.append("carrousel", JSON.stringify(formBody));

      await carrouselAction.update(dispatch, carrouselId, formData);
      setAlertMessage(`Carrousel visibility updated to: ${selectedOption.label}`);
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      // Refresh the list to reflect the change
      carrouselAction.fetchAll(dispatch, initialParams);
    } catch (error) {
      setErrorMessage('Failed to update carrousel visibility');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  return (
    <List
      showAlert={showAlert}
      setShowAlert={setShowAlert}
      successAlertMessage={alertMessage || "Slide duration was successfully updated."}
      customListError={errorMessage}
      sortIsDisabled={false}
      reOrderAction={(body, filterParams) => { carrouselAction.reOrder(dispatch, body, filterParams) }}
      redirect="/carrousel/form"
      title={t("carrousel.carrousel")}
      isOverview={false}
      defaultFieldToSortBy="order"
      tooltipTextDelete="Delete Carrousel"
      columnNames={[
        "",
        "IMAGE",
        "TITLE",
        "STATUS",
        "DESCRIPTION",
        "URL",
        "VISIBLE IN",
        "Portal Visibility",
      ]}
      hasImage={true}
      entities={carrousels?.data?.map(
        ({
          id,
          attributes: {
            image,
            title,
            active,
            description,
            url,
            locations,
            location_groups,
            order,
            portal_visibility
          },
        }) => ({
          order,
          id,
          image: image?.data?.attributes?.url
            ? `${image?.data?.attributes?.url}?${token}`
            : placeholder,
          columns: [
            { primary: title },
            { primary: active ? "ACTIVE" : "INACTIVE" },
            { primary: description },
            { primary: url ? <div className="url-cell" title={url}>{url}</div> : '' },
            {
              primary: isAll(locations, location_groups) ? (
                "All Members"
              ) : (
                <Select
                  theme={(theme) => getTheme(theme)}
                  className="form-select-custom"
                  placeholder="Show"
                  isMulti
                  styles={customStylesForTable}
                  isOptionDisabled={() => true}
                  options={memberOptions[id]}
                  formatGroupLabel={formatGroupLabel}
                  getOptionLabel={(option) => (
                    <div className="form-select-custom-label">
                      <span className="form-select-custom-label-primary">
                        {option.label}
                      </span>
                    </div>
                  )}
                />
              ),
            },
            {
              primary: (
                <PortalVisibilitySelect
                  value={portal_visibility}
                  onChange={handleVisibilityChange(id)}
                  compact={true}
                />
              )
            },
          ],
          name: title,
        })
      )}
      fieldsToSort={carrouselFields}
      fetchAction={(params) => carrouselAction.fetchAll(dispatch, { ...initialParams, ...params })}
      metadata={meta}
      deleteAction={(id, params) =>
        carrouselAction.remove(dispatch, id, params)
      }
      loading={listLoading}
    />
  );
};

export default CarrouselList;
